import { officeAuthHeaders } from '../officeAuth';
import { officeUrl } from '../officeApi';
import { clearLegacyOfficeHistoryIfCovered, getOfficeConversation, replaceOfficeConversationIndex, saveOfficeConversation } from '../officeStorage';
import type { OfficeArtifact, OfficeConversation, OfficeMessage, OfficeQuestion, OfficeSource } from '../types';
import { orderOfficeMessages } from '../officeMessageOrder';

const endpoint = '/api/v2/office/history';

export class OfficeHistoryRequestError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Office history request failed (${status})`);
    this.status = status;
  }
}

export const isOfficeHistoryAuthorizationError = (error: unknown): boolean => error instanceof OfficeHistoryRequestError && [401, 403].includes(error.status);

interface ConversationWire {
  id: string;
  title: string;
  version: number;
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface MessageWire {
  id: string;
  turn_id: string;
  sequence: number;
  role: 'user' | 'assistant';
  content: string;
  status: string;
  language: 'zh-CN' | 'zh-HK' | 'en-US';
  job_id?: string;
  attempt?: number;
  request_context?: { meetingContext?: boolean };
  plan?: OfficeMessage['plan'];
  progress?: OfficeMessage['progress'];
  question?: OfficeQuestion & { status?: string };
  artifacts?: OfficeArtifact[];
  sources?: OfficeSource[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(officeUrl(url), {
    credentials: 'include',
    cache: 'no-store',
    ...init,
    headers: { ...officeAuthHeaders(), ...(init?.headers || {}) },
  });
  if (!response.ok) {
    throw new OfficeHistoryRequestError(response.status);
  }
  return response.json() as Promise<T>;
};

const timestamp = (value: string): number => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Date.now();
};

const conversation = (wire: ConversationWire, messages: OfficeMessage[] = []): OfficeConversation => ({
  id: wire.id,
  title: wire.title,
  createdAt: timestamp(wire.created_at),
  updatedAt: timestamp(wire.updated_at),
  messages,
});

const message = (wire: MessageWire): OfficeMessage => ({
  id: wire.id,
  turnId: wire.turn_id,
  sequence: wire.sequence,
  role: wire.role,
  content: wire.content,
  createdAt: timestamp(wire.created_at),
  artifacts: (wire.artifacts || []).map(item => ({
    ...item,
    download_url: `/api/v1/ai-worker/artifacts/${item.artifact_id}`,
  })),
  sources: wire.sources || [],
  progress: wire.progress || [],
  plan: wire.plan || [],
  ...(wire.role === 'assistant' ? { jobState: wire.status } : {}),
  ...(wire.job_id ? { jobId: wire.job_id } : {}),
  ...(wire.attempt ? { attempt: wire.attempt } : {}),
  ...(wire.completed_at ? { completedAt: timestamp(wire.completed_at) } : {}),
  ...(wire.question?.status === 'pending' ? { question: wire.question as OfficeQuestion } : {}),
  ...(wire.role === 'user' ? { request: { meetingContext: Boolean(wire.request_context?.meetingContext) } } : {}),
  language: wire.language,
});

export const refreshOfficeConversationIndex = async (userId: string): Promise<OfficeConversation[]> => {
  const result = await request<{ items: ConversationWire[] }>(`${endpoint}/conversations?limit=100`);
  const entries = result.items.map(item => conversation(item, getOfficeConversation(userId, item.id)?.messages || []));
  replaceOfficeConversationIndex(userId, entries);
  clearLegacyOfficeHistoryIfCovered(userId, new Map(result.items.map(item => [item.id, item.message_count])));
  return entries;
};

export const loadOfficeSession = async (): Promise<{ user_id: string }> => request<{ user_id: string }>(`${endpoint}/session`);

export const loadOfficeConversation = async (userId: string, conversationId: string): Promise<OfficeConversation> => {
  const encoded = encodeURIComponent(conversationId);
  const [header, result] = await Promise.all([
    request<ConversationWire>(`${endpoint}/conversations/${encoded}`),
    request<{ items: MessageWire[] }>(`${endpoint}/conversations/${encoded}/messages?limit=100`),
  ]);
  const loaded = conversation(header, orderOfficeMessages(result.items.map(message)));
  saveOfficeConversation(userId, loaded);
  return loaded;
};

export const deleteOfficeConversationRemote = async (conversationId: string): Promise<void> => {
  const response = await fetch(officeUrl(`${endpoint}/conversations/${encodeURIComponent(conversationId)}`), {
    method: 'DELETE',
    credentials: 'include',
    cache: 'no-store',
    headers: officeAuthHeaders(),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Office history deletion failed (${response.status})`);
  }
};
